/**
 * Re-run — measure a parser fix against the same corpus.
 *
 * Feeds this record's stored originals back through the engine as it is NOW,
 * and shows what changed against what is stored. Nothing is written: the
 * record the desk works from is never modified by measuring it, and the
 * dialog says so.
 */

import { useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ArrowRight, Loader2, RefreshCw, X } from 'lucide-react'

import { attachmentStatusLabel } from '@/components/intake/AttachmentStatusBadge'
import { ingestionService } from '@/services/ingestion'
import { apiErrorText } from '@/utils/apiErrorText'

function fmt(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'number') return Number.isInteger(v) ? v.toLocaleString() : v.toString()
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

export function RerunDialog({
  ingestionId,
  onClose,
}: {
  ingestionId: string
  onClose: () => void
}) {
  const rerun = useMutation({
    mutationFn: () => ingestionService.rerun(ingestionId),
  })

  useEffect(() => {
    rerun.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingestionId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const data = rerun.data
  const err = rerun.error as { response?: { data?: { detail?: string } } } | null

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Re-run parser"
    >
      <div className="flex max-h-[86vh] w-[min(820px,94vw)] flex-col rounded-xl border border-amber-400/20 bg-[#0d0d0d] shadow-2xl">
        <div className="flex items-center justify-between border-b border-amber-400/10 px-4 py-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-stone-500">
              Re-run parser
            </div>
            <div className="text-[13px] text-stone-200">
              Same originals, the engine as it is now. The stored record is not changed.
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 text-stone-500 hover:bg-stone-800 hover:text-stone-200">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {rerun.isPending && (
            <div className="flex h-40 items-center justify-center gap-2 text-[12px] text-stone-500">
              <Loader2 size={14} className="animate-spin" /> Re-parsing the stored originals…
            </div>
          )}
          {rerun.isError && (
            <p className="text-[12px] text-red-300">
              {apiErrorText(err, 'The re-run could not be completed.')}
            </p>
          )}
          {data && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[11px]">
                <span className="text-stone-400">
                  Re-ran from <span className="font-mono text-stone-200">{data.reran_from}</span>
                </span>
                <span className={data.changed_count ? 'text-amber-300' : 'text-emerald-300'}>
                  {data.changed_count} changed · {data.unchanged_count} unchanged
                </span>
                <span className="font-mono text-[10px] text-stone-600">persisted: {String(data.persisted)}</span>
              </div>

              {data.changed_count === 0 ? (
                <p className="rounded border border-emerald-400/20 bg-emerald-400/5 px-3 py-2 text-[11px] text-emerald-200">
                  The current engine reads this package exactly as it was stored. No drift.
                </p>
              ) : (
                <div className="overflow-x-auto rounded border border-stone-800">
                  <table className="w-full text-[11px]">
                    <thead className="bg-black/40 text-left font-mono text-[10px] uppercase tracking-wider text-stone-500">
                      <tr>
                        <th className="px-2 py-1.5">Field</th>
                        <th className="px-2 py-1.5">Stored</th>
                        <th className="px-2 py-1.5"></th>
                        <th className="px-2 py-1.5">Now</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.changed.map((c) => (
                        <tr key={c.field} className="border-t border-stone-800/70">
                          <td className="px-2 py-1.5 font-mono text-stone-300">{c.field.replace(/^fields\./, '')}</td>
                          <td className="px-2 py-1.5 text-stone-500">{fmt(c.before)}</td>
                          <td className="px-1 py-1.5 text-stone-600"><ArrowRight size={11} /></td>
                          <td className="px-2 py-1.5 text-stone-200">{fmt(c.after)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {data.fresh_attachment_statuses.length > 0 && (
                <div className="text-[10px] text-stone-500">
                  Attachments this run:{' '}
                  {data.fresh_attachment_statuses
                    .map((a) => `${a.filename} (${attachmentStatusLabel(a.status).label})`)
                    .join(' · ')}
                </div>
              )}
              {data.fresh_extraction_notes && (
                <p className="text-[10px] text-stone-600">{data.fresh_extraction_notes}</p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-amber-400/10 px-4 py-3">
          <span className="text-[10px] text-stone-600">
            Measures the engine. To adopt a better reading, archive and re-parse from the record.
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => rerun.mutate()}
              disabled={rerun.isPending}
              className="flex items-center gap-1.5 rounded-lg border border-stone-700 px-3 py-1.5 text-[11px] text-stone-300 hover:border-amber-400/40 hover:text-amber-200 disabled:opacity-50"
            >
              <RefreshCw size={11} className={rerun.isPending ? 'animate-spin' : ''} /> Run again
            </button>
            <button type="button" onClick={onClose} className="rounded-lg border border-amber-400/40 px-3 py-1.5 text-[11px] font-medium text-amber-300 hover:bg-amber-400/10">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RerunDialog
